/**
 * Shared auth actions for marketing and dashboard views.
 *
 * The AccountController's Logout action is [HttpPost] only (for CSRF protection), so a
 * plain <a href="/Account/Logout"> returns 405 Method Not Allowed. Every UI surface
 * that offers a Logout affordance goes through `logout()` below, which submits a
 * hidden form carrying the antiforgery token.
 */

import Loading from 'quasar/src/plugins/loading/Loading.js';;

/**
 * Log the current user out.
 *
 * Submits a dynamically-created POST form to /Account/Logout with the antiforgery
 * token in the form body. A browser navigation (not a fetch) is required because:
 *
 *   1. The server returns a 302 chain that may end at the IdP's end-session /
 *      SAML SLO endpoint on a different origin. fetch() follows same-origin
 *      redirects invisibly but stalls on cross-origin redirects (CORS
 *      opaqueredirect); a browser navigation handles them natively.
 *   2. SAML SLO only fires when the browser actually visits the IdP's logout URL
 *      with a SAMLRequest query parameter. A fetch that then sets
 *      window.location.href = '/Account/Login' short-circuits the SLO step, so
 *      the IdP session stays alive. Browser navigation executes the whole chain.
 *   3. The prior fetch-based implementation waited for ALL server-side redirects
 *      to resolve before Vue ran window.location.href, producing 2-5s of "nothing
 *      is happening" UI delay. A form submit hands control to the browser
 *      immediately and the user sees the normal navigation indicator.
 *
 * Quasar Loading overlay gives instant click feedback for the brief window
 * before the browser starts navigating.
 */
export function logout() {
    Loading.show({ message: 'Signing you out...' });

    const token = document.querySelector('input[name="__RequestVerificationToken"]')?.value || '';

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/Account/Logout';
    form.style.display = 'none';

    const tokenInput = document.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = '__RequestVerificationToken';
    tokenInput.value = token;
    form.appendChild(tokenInput);

    document.body.appendChild(form);
    form.submit();
}
